import type { KeyboardEvent, RefObject } from "react";

import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import {
  Box,
  Flex,
  IconButton,
  Popover,
  Reset,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Popover as PopoverPrimitive } from "radix-ui";

import { cn } from "~/v2/utils/cn";
import type { WatchlistAddSearchMatch } from "./WatchlistAddSearch.derive";

export interface WatchlistAddSearchPresentationProps {
  anchorRef: RefObject<HTMLDivElement | null>;
  query: string;
  onQueryChange: (value: string) => void;
  results: WatchlistAddSearchMatch[];
  firstAvailable?: string;
  error?: string;
  /** Add mutation in flight — distinct from `isSearching` so a pending add never looks like a failed search. */
  isPending: boolean;
  /** Search request in flight — suppresses "No matching symbols" while results are still empty. */
  isSearching: boolean;
  onAdd: (symbol: string) => void;
  onClear: () => void;
}

/**
 * Header symbol search for a bound watchlist. Radix owns the popover's
 * portal placement, width-matching against the anchor, and outside-click —
 * open state is simply derived from whether there is a query to show
 * results for.
 */
export const WatchlistAddSearchPresentation = ({
  anchorRef,
  query,
  onQueryChange,
  results,
  firstAvailable,
  error,
  isPending,
  isSearching,
  onAdd,
  onClear,
}: WatchlistAddSearchPresentationProps) => {
  const isOpen = query.trim().length > 0;

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (isPending) {
        return;
      }
      if (firstAvailable) {
        onAdd(firstAvailable);
      }
      return;
    }
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) {
          onClear();
        }
      }}
    >
      <PopoverPrimitive.Anchor asChild>
        <div ref={anchorRef as RefObject<HTMLDivElement>}>
          <TextField.Root
            size="1"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search and add symbol"
            aria-label="Search symbols to add to watchlist"
          >
            <TextField.Slot>
              <MagnifyingGlassIcon size={14} aria-hidden />
            </TextField.Slot>
            {query ? (
              <TextField.Slot side="right">
                <IconButton
                  type="button"
                  size="1"
                  variant="ghost"
                  color="gray"
                  aria-label="Clear symbol search"
                  onClick={onClear}
                >
                  <XIcon size={14} aria-hidden />
                </IconButton>
              </TextField.Slot>
            ) : null}
          </TextField.Root>
        </div>
      </PopoverPrimitive.Anchor>

      <Popover.Content
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => {
          const target = event.detail.originalEvent.target as Node | null;
          if (target && anchorRef.current?.contains(target)) {
            event.preventDefault();
          }
        }}
        className="flex w-[var(--radix-popover-trigger-width)] flex-col gap-1 p-2"
      >
        {error ? (
          <Text size="1" color="red" role="alert">
            {error}
          </Text>
        ) : null}

        {isPending ? (
          <Text size="1" color="gray">
            Adding…
          </Text>
        ) : isSearching && results.length === 0 ? (
          <Text size="1" color="gray">
            Searching…
          </Text>
        ) : results.length === 0 ? (
          <Text size="1" color="gray">
            No matching symbols
          </Text>
        ) : (
          <Flex direction="column" gap="1">
            {results.map((result) => (
              <Reset key={result.symbol}>
                <button
                  type="button"
                  disabled={result.added}
                  onClick={() => onAdd(result.symbol)}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 rounded-[var(--radius-2)] px-2 py-1.5 text-left focus-visible:shadow-[inset_0_0_0_2px_var(--focus-8)]",
                    result.added
                      ? "cursor-not-allowed opacity-60"
                      : "hover:bg-[var(--gray-a3)]",
                  )}
                >
                  <Box minWidth="0">
                    <Text as="p" size="2" weight="medium" truncate m="0">
                      {result.symbol}
                    </Text>
                    <Text as="p" size="1" color="gray" truncate m="0">
                      {result.name}
                    </Text>
                  </Box>
                  <Text size="1" color={result.added ? "gray" : undefined}>
                    {result.added ? "Added" : "Add"}
                  </Text>
                </button>
              </Reset>
            ))}
          </Flex>
        )}
      </Popover.Content>
    </Popover.Root>
  );
};
